import moment from 'moment';
import EcsConversation from '@/components/ecs-conversation/EcsConversation';
import { MESSAGE_ICON, MESSAGE_TYPE } from '@/constants/conversation/constants';
import { ELEMENT_STATE } from '@/constants/element/constants';
import { INTERACTION_STATE, INTERACTION_TRANSFER } from '@/constants/interaction/constants';
import { MEDIA_TYPE_CHAT, MEDIA_TYPE_SMS } from '@/constants/socket-events';
import InteractionService from '@/services/interactionService';
import { downloadConversation, downloadEmails } from '@/utils';

export const pendingConversations = {};

const socketEventActions = {
  SOCKET_EVENT_ELEMENT_STATE_CHANGED_EVENT({ dispatch }, event) {
    const interactionMediaType = event.interactionMediaType.toUpperCase();
    if (![MEDIA_TYPE_CHAT, MEDIA_TYPE_SMS].includes(interactionMediaType)) {
      return;
    }

    const messageData = helpers.getMessageDataFromEvent(event);

    if (event.oldElementStateId === ELEMENT_STATE.created) {
      dispatch('startConversation', event);
    }

    if (
      event.oldElementStateId === ELEMENT_STATE.diverted &&
      event.newElementStateId === ELEMENT_STATE.active &&
      interactionMediaType === MEDIA_TYPE_SMS
    ) {
      dispatch('startConversation', event);
      return;
    }

    switch (event.newElementStateId) {
      case ELEMENT_STATE.diverted:
        dispatch('addSystemMessage', {
          ...messageData,
          icon: MESSAGE_ICON.onHold,
          i18nKey: ELEMENT_STATE.diverted,
          oldState: event.oldElementStateId
        });
        break;

      case ELEMENT_STATE.active:
        dispatch('addSystemMessage', {
          ...messageData,
          icon: MESSAGE_ICON.resumeHold,
          i18nKey: ELEMENT_STATE.active,
          oldState: event.oldElementStateId
        });
        break;

      case ELEMENT_STATE.closed:
        dispatch('addSystemMessage', {
          ...messageData,
          icon: MESSAGE_ICON.leftConversation,
          i18nKey: ELEMENT_STATE.closed,
          oldState: event.oldElementStateId
        });
        break;

      case ELEMENT_STATE.abandoned:
        dispatch('addSystemMessage', {
          ...messageData,
          icon: MESSAGE_ICON.leftConversation,
          i18nKey: ELEMENT_STATE.abandoned,
          oldState: event.oldElementStateId
        });
        dispatch(
          'NOTIFICATIONS/addUnreadMessageNotification',
          {
            elementId: messageData.elementId,
            title: MESSAGE_TYPE.system,
            description: ELEMENT_STATE.abandoned
          },
          { root: true }
        );
        break;

      case ELEMENT_STATE.activating:
        dispatch('addSystemMessage', {
          ...messageData,
          icon: MESSAGE_ICON.activating,
          i18nKey: ELEMENT_STATE.activating,
          oldState: event.oldElementStateId
        });
        break;

      case ELEMENT_STATE.conferenced:
        dispatch('addSystemMessage', {
          ...messageData,
          icon: MESSAGE_ICON.merge,
          i18nKey: ELEMENT_STATE.conferenced,
          oldState: event.oldElementStateId
        });
        break;

      case ELEMENT_STATE.conferencedDiverted:
        dispatch('addSystemMessage', {
          ...messageData,
          icon: MESSAGE_ICON.onHold,
          i18nKey: ELEMENT_STATE.conferencedDiverted,
          oldState: event.oldElementStateId
        });
        break;

      case ELEMENT_STATE.cancelled:
        dispatch('removeConversation', event);
        break;
    }
  },
  SOCKET_EVENT_INTERACTION_CONFERENCE_STARTED_EVENT({ dispatch, getters }, event) {
    const conversations = getters.getConversationsForInteraction(event.interactionId);
    conversations.forEach(({ elementId }) => {
      dispatch('addSystemMessage', {
        originator: {},
        elementId,
        icon: MESSAGE_ICON.merge,
        i18nKey: INTERACTION_STATE.inConference
      });
    });
  },
  SOCKET_EVENT_INTERACTION_CONFERENCE_STOPPED_EVENT({ dispatch, getters }, event) {
    const conversations = getters.getConversationsForInteraction(event.interactionId);
    conversations.forEach(({ elementId }) => {
      dispatch('addSystemMessage', {
        originator: {},
        elementId,
        icon: MESSAGE_ICON.split,
        i18nKey: INTERACTION_STATE.consulting
      });
    });
  },
  SOCKET_EVENT_ATTENDED_INTERACTION_TRANSFERRED_TO_AGENT_EVENT({ dispatch }, event) {
    dispatch('addSystemMessage', {
      originator: {},
      elementId: event.elementId,
      icon: MESSAGE_ICON.forward,
      i18nKey: INTERACTION_TRANSFER.attended
    });
  },
  SOCKET_EVENT_CHAT_MESSAGE_STATUS_CHANGED_EVENT({ dispatch }, event) {
    const messageData = helpers.getMessageDataFromEvent(event);
    switch (event.messageStatus.toUpperCase()) {
      case MESSAGE_TYPE.system:
        // TODO: Temporary fix to set agent as originator of system messages, remove after ECS fixes originator
        const newMessageData = {
          ...messageData,
          originator: {
            name: event.agentName,
            id: event.agentId
          }
        };
        dispatch('addSystemMessage', {
          icon: MESSAGE_ICON.notification,
          ...newMessageData
        });
        break;
      case MESSAGE_TYPE.regular:
        // ------ Temporary fix until ECS team fix this. -------
        if (event.messageContent.startsWith('^!^%')) {
          const isTyping = event.messageContent.startsWith('^!^%Typing');
          dispatch('setIsTyping', { ...messageData, isTyping });
          return;
        }
        // ---------------- end temporary fix ------------------

        dispatch('addMessageToConversation', messageData);
        break;
      case MESSAGE_TYPE.isTyping:
        dispatch('setIsTyping', { ...messageData, isTyping: true });
        break;
      case MESSAGE_TYPE.idle:
      case MESSAGE_TYPE.textEntered:
        dispatch('setIsTyping', { ...messageData, isTyping: false });
        break;
      default:
        break;
    }
  },
  SOCKET_EVENT_SMS_MESSAGE_STATUS_CHANGED_EVENT({ dispatch, rootGetters }, event) {
    const messageData = helpers.getMessageDataFromEvent(event);
    if (event.messageStatus.toUpperCase() === MESSAGE_TYPE.delivered) {
      if (messageData.originator.name !== rootGetters['AGENT/loggedInAgent'].name) {
        dispatch('addMessageToConversation', messageData);
      }
    }
  }
};

const stateActions = {
  restoreConversation: ({ dispatch, getters }, event) => {
    const conversation = getters.getConversationForElement(event.elementId);

    if (!conversation) {
      event.messages = [
        {
          elementId: event.elementId,
          type: MESSAGE_TYPE.system,
          icon: MESSAGE_ICON.resumeHold,
          i18nKey: `interaction-restored`,
          originator: {}
        }
      ];
      dispatch('startConversation', event);
    }
  },

  startConversation: ({ commit }, event) => {
    const conversation = new EcsConversation(event);

    if (pendingConversations[event.interactionId]) {
      conversation.messages = [...pendingConversations[event.interactionId]];
      delete pendingConversations[event.interactionId];
    }

    commit('createConversation', conversation);
  },

  removeConversation: ({ commit }, { elementId }) => {
    commit('deleteConversation', elementId);
  },

  addMessageToConversation: (
    { commit, dispatch, rootGetters },
    { elementId, originator, content, timestamp, icon }
  ) => {
    const message = {
      type: MESSAGE_TYPE.regular,
      icon,
      originator,
      content,
      timestamp
    };
    commit('removeTypingParticipant', { elementId, originator });
    commit('addMessage', { elementId, message });
    if (originator.id !== rootGetters['AGENT/loggedInAgent'].id) {
      dispatch(
        'NOTIFICATIONS/addUnreadMessageNotification',
        { elementId, title: originator.name, description: content },
        { root: true }
      );
    }
  },

  addSystemMessage: ({ commit }, { elementId, originator, content, timestamp, icon, i18nKey, oldState }) => {
    const message = {
      type: MESSAGE_TYPE.system,
      icon,
      originator,
      content,
      timestamp,
      i18nKey,
      oldState
    };
    commit('removeTypingParticipant', { elementId, originator });
    commit('addMessage', { elementId, message });
  },

  setIsTyping: ({ commit }, { elementId, originator, isTyping }) => {
    if (isTyping) {
      commit('addTypingParticipant', { elementId, originator });
    } else {
      commit('removeTypingParticipant', { elementId, originator });
    }
  },

  setConversation: ({ commit, getters }, { interactionId, messages, replaceConversation }) => {
    const conversations = getters.getConversationsForInteraction(interactionId);
    if (conversations.length) {
      if (replaceConversation) {
        commit('setMessagesToConversation', { elementId: conversations[0].elementId, messages });
      } else {
        commit('addMessagesToConversation', { elementId: conversations[0].elementId, messages });
      }
    } else {
      // Blind transfer.
      const notificationMessage = {
        type: MESSAGE_TYPE.system,
        icon: MESSAGE_ICON.forward,
        originator: {},
        timestamp: moment.now(),
        i18nKey: INTERACTION_TRANSFER.blind
      };
      pendingConversations[interactionId] = [notificationMessage, ...messages];
    }
  },

  loadInteractionSmsHistory: (
    { commit },
    {
      interactionId,
      firstParty,
      secondParty,
      maxNumberOfMessages,
      elementId,
      maxNumber,
      earliestTime,
      latestTime,
      fullHistory
    }
  ) => {
    return InteractionService.getHistorySMSMessages(
      interactionId,
      firstParty,
      secondParty,
      maxNumber,
      earliestTime,
      maxNumberOfMessages,
      latestTime,
      fullHistory
    ).then(response => {
      commit('loadInteractionSmsHistory', {
        interactionId,
        messages: response.data,
        elementId
      });
    });
  }
};

const conversationActions = {
  downloadChat: ({ getters }, data) => {
    const isOutgoingEmail = data.interaction.interactionType === 'OUT. EXTERNAL MAIL';

    const customer = {
      name:
        data.interaction.interactionMediaType === 'EMAIL'
          ? data.interaction.originatorContact.name
          : data.interaction.receptor.firstName || data.interaction.name,
      email: isOutgoingEmail
        ? data.interaction.routing.destination
        : data.interaction.receptor.email || data.interaction.receptor.endPoint
    };

    if (data.emailContent) {
      const emailData = isOutgoingEmail
        ? [data.emailContent.outgoingEmailData, data.emailContent.incomingEmailData]
        : [data.emailContent.incomingEmailData, data.emailContent.outgoingEmailData];
      const emails = [];
      emailData.forEach(email => {
        if (email.body) {
          emails.push(email);
        }
      });
      downloadEmails(customer, emails, data.interaction.interactionType);
    } else {
      const interaction = getters.getConversationsForInteraction(data.interaction)[0];
      const conversation = {
        agent: data.agent,
        messages: interaction.messages
      };
      downloadConversation(customer, conversation, data.interaction.interactionMediaType);
    }
  }
};

const helpers = {
  getMessageDataFromEvent({
    interactionId,
    messageContent,
    originatorName,
    elementId,
    timestamp,
    originatorId,
    senderName
  }) {
    return {
      interactionId,
      elementId,
      content: messageContent,
      originator: {
        name: originatorName || senderName,
        id: originatorId || ''
      },
      timestamp
    };
  }
};

export default {
  ...socketEventActions,
  ...stateActions,
  ...conversationActions
};
